import { Injectable } from "@angular/core";
import { ToastService } from "./toast.service";
import { LanguageEnum } from "../constants/language.enum";
import { SourceViews } from "../constants/source-views";

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  private readonly keys = {
    signingImage: 'SigningImage',
    versionNumber: 'StoredVersionNumber',
    language: 'Language',
    dashboardColumns: 'DashboardColumns',
    dashboardPickers: 'DashboardPickers',
    dashboardSorting: 'DashboardSorting',
    showNewDashboard: 'showNewDashboard',
    lastSelectedRequestType: 'lastSelectedRequestType',
    itemsPerPage: 'itemsPerPage',
    closableWrapperCollapsed: 'closableWrapperCollapsed',
  } as const;

  constructor(private toastService: ToastService) {}

  private read(key: keyof typeof this.keys): string | null {
    return localStorage.getItem(this.keys[key]);
  }

  private write(key: keyof typeof this.keys, value: string | null): void {
    if (value == null) localStorage.removeItem(this.keys[key]);
    else localStorage.setItem(this.keys[key], value);
  }

  private has(key: keyof typeof this.keys): boolean {
    return localStorage.getItem(this.keys[key]) != null;
  }

  // --- Language ---

  get Language(): LanguageEnum | string {
    const stored = this.read('language');
    return stored ?? LanguageEnum.NL;
  }

  set Language(value: LanguageEnum | string) {
    const normalized = typeof value === 'string' ? value.toLocaleLowerCase() : value;
    this.write('language', normalized);
  }

  private _sourceViewType: SourceViews | null = null;
  public set SourceViewType(newSourceViewType: SourceViews) {
    this._sourceViewType = newSourceViewType;
    localStorage.setItem("SourceViewType", newSourceViewType);
  }

  public get SourceViewType(): SourceViews | null {
    if (!this._sourceViewType) {
      const storedValue = localStorage.getItem("SourceViewType");
      if (storedValue) {
        this._sourceViewType = storedValue as SourceViews;
      }
    }
    return this._sourceViewType;
  }









  // --- Signing image ---

  get signingImage(): string | null {
    return this.read('signingImage');
  }

  set signingImage(value: string | null) {
    this.write('signingImage', value);
  }

  get hasSigningImage(): boolean {
    return this.has('signingImage');
  }

  // --- Version number ---

  verifyStoredVersionNumber(versionNumber: string, urlForPatchNotes: string): void {
    const previous = this.read('versionNumber');
    if (previous !== versionNumber) {
      if (this.toastService.ShouldShowNewVersionMessage(previous ?? versionNumber, versionNumber)) {
        this.toastService.DisplayNewVersion(versionNumber, urlForPatchNotes);
      }
      this.write('versionNumber', versionNumber);
    }
  }

  // --- Closable wrapper ---

  get closableWrapperCollapsed(): string | null {
    return this.read('closableWrapperCollapsed');
  }

  set closableWrapperCollapsed(value: string | null) {
    this.write('closableWrapperCollapsed', value);
  }

  get hasClosableWrapperCollapsed(): boolean {
    return this.has('closableWrapperCollapsed');
  }

  // --- Dashboard columns ---

  get dashboardColumns(): string | null {
    return this.read('dashboardColumns');
  }

  set dashboardColumns(value: string | null) {
    this.write('dashboardColumns', value);
  }

  get hasDashboardColumns(): boolean {
    return this.has('dashboardColumns');
  }

  // --- Dashboard pickers ---

  get dashboardPickers(): string | null {
    return this.read('dashboardPickers');
  }

  set dashboardPickers(value: string | null) {
    this.write('dashboardPickers', value);
  }

  get hasDashboardPickers(): boolean {
    return this.has('dashboardPickers');
  }

  // --- Dashboard sorting ---

  get dashboardSorting(): string | null {
    return this.read('dashboardSorting');
  }

  set dashboardSorting(value: string | null) {
    this.write('dashboardSorting', value);
  }

  get hasDashboardSorting(): boolean {
    return this.has('dashboardSorting');
  }

  // --- Show new dashboard ---

  get showNewDashboard(): string | null {
    return this.read('showNewDashboard');
  }

  set showNewDashboard(value: string | null) {
    // Treat empty string the same as null
    this.write('showNewDashboard', value || null);
  }

  get hasShowNewDashboard(): boolean {
    return this.has('showNewDashboard');
  }

  // --- Last selected request type ---

  get lastSelectedRequestType(): string | null {
    return this.read('lastSelectedRequestType');
  }

  set lastSelectedRequestType(value: string | null) {
    this.write('lastSelectedRequestType', value);
  }

  // --- Items per page ---

  get itemsPerPage(): number {
    const stored = this.read('itemsPerPage');
    return stored != null ? parseInt(stored, 10) : 10;
  }

  set itemsPerPage(value: number) {
    this.write('itemsPerPage', value?.toString());
  }

  // --- Column widths ---

  removeStorageColumnWidth(columnStorageName: string): void {
    localStorage.removeItem(columnStorageName);
  }
}
